import { useMemo, useState } from 'react';
import { Box, IconButton, makeStyles } from '@material-ui/core';
import CloseRoundedIcon from '@material-ui/icons/CloseRounded';
import ReplayRoundedIcon from '@material-ui/icons/ReplayRounded';
import PeopleAltOutlinedIcon from '@material-ui/icons/PeopleAltOutlined';
import { SAutocomplete, SText } from '@setvi/shared/components';
import { SAutocompleteItem } from '@setvi/shared/components/sautocomplete/interfaces';
import { getInitials } from 'Utils/getInitials';
import { RfqUserAvatar } from 'pages/auto-rfq/components/user-avatar';
import { RfqInternalShareEntry } from 'Services/Query/Auto-RFQ/Types';
import { useCompanyUserSearch } from './hooks/use-company-user-search';

const useStyles = makeStyles(({ spacing }) => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing(3)
  },
  label: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing(1),
    fontSize: 13,
    fontWeight: 600,
    color: '#20262D',
    marginBottom: spacing(1)
  },
  count: {
    fontSize: 12,
    fontWeight: 600,
    color: '#5D7083',
    background: '#F1F4F7',
    borderRadius: 10,
    padding: '1px 8px'
  },
  hint: {
    fontSize: 12,
    color: '#8A97A5',
    marginTop: spacing(0.75)
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing(1),
    // Show ~5 rows, then scroll (≈5 × 48px row + 4 × 8px gap).
    maxHeight: 272,
    overflowY: 'auto',
    paddingRight: spacing(0.5)
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing(2),
    padding: spacing(1, 1.5),
    borderRadius: 10,
    transition: 'background-color 150ms ease-out',
    '&:hover': {
      background: '#F8F9FA'
    }
  },
  rowText: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0
  },
  tag: {
    fontSize: 11,
    fontWeight: 600,
    color: '#1E96FC',
    background: '#E8F4FE',
    borderRadius: 12,
    padding: '2px 10px',
    whiteSpace: 'nowrap'
  },
  removedTag: {
    fontSize: 11,
    fontWeight: 600,
    color: '#EF4444',
    background: '#FDECEC',
    borderRadius: 12,
    padding: '2px 10px',
    whiteSpace: 'nowrap'
  },
  removeBtn: {
    color: '#8A97A5',
    transition: 'background-color 150ms ease-out, color 150ms ease-out',
    '&:hover': {
      background: '#FDECEC',
      color: '#EF4444'
    }
  },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: spacing(1),
    padding: spacing(3, 2),
    borderRadius: 12,
    border: '1px dashed #DCE3EA',
    background: '#F8FAFC'
  },
  emptyIcon: {
    color: '#B7C2CD',
    fontSize: 28
  }
}));

// A teammate picked from the search but not yet shared with — staged until the
// user commits via the "Share" button.
export interface PendingShareUser {
  email: string;
  name?: string;
  userId?: string;
  firstName?: string;
  lastName?: string;
}

export interface ShareManagerProps {
  entries: RfqInternalShareEntry[];
  ownerId?: string;
  currentUserId?: string;
  currentUserEmail?: string;
  pendingUsers: PendingShareUser[];
  // Recipients staged for removal — flagged with a "Removed" badge until the
  // footer button commits the change.
  removedUserIds: string[];
  onSelectPending: (user: PendingShareUser) => void;
  onRemovePending: (email: string) => void;
  onToggleRemove: (userId: string) => void;
  // Noun shown in copy (e.g. "Share this RFQ" / "Quote") so the dialog can be
  // reused across modules. Defaults to "RFQ".
  entityLabel?: string;
}

export const ShareManager = ({
  entries,
  ownerId,
  currentUserId,
  currentUserEmail,
  pendingUsers,
  removedUserIds,
  onSelectPending,
  onRemovePending,
  onToggleRemove,
  entityLabel = 'RFQ'
}: ShareManagerProps) => {
  const classes = useStyles();
  const [searchValue, setSearchValue] = useState('');
  const { users, isLoading, fetchMore } = useCompanyUserSearch(searchValue);

  const sharedEmails = useMemo(
    () => new Set(entries.map(e => e.email?.toLowerCase())),
    [entries]
  );

  const pendingEmails = useMemo(
    () => new Set(pendingUsers.map(p => p.email.toLowerCase())),
    [pendingUsers]
  );

  const options: SAutocompleteItem[] = useMemo(
    () =>
      users
        .filter(
          u =>
            !sharedEmails.has(u.email?.toLowerCase()) &&
            u.email?.toLowerCase() !== currentUserEmail?.toLowerCase() &&
            !pendingEmails.has(u.email?.toLowerCase())
        )
        .map(u => ({
          id: u.email,
          name: `${u.firstName} ${u.lastName}`.trim() || u.email,
          subtitle: u.email,
          icon: (
            <RfqUserAvatar
              size="sm"
              userId={u._id}
              value={getInitials({
                firstName: u.firstName,
                lastName: u.lastName,
                email: u.email
              })}
            />
          )
        })),
    [users, sharedEmails, currentUserEmail, pendingEmails]
  );

  const handleItemClick = (item: SAutocompleteItem) => {
    // Options set `id` to the user's email; fall back to the typed text when it
    // looks like an email (SAutocomplete's "add new" path).
    const email = String(item.id).includes('@') ? String(item.id) : item.name;
    if (!email.includes('@')) return;
    // Stage the pick (with the matching user's details for a richer preview)
    // instead of sharing immediately — the actual add happens on "Share".
    const match = users.find(
      u => u.email?.toLowerCase() === email.toLowerCase()
    );
    onSelectPending({
      email: email.trim(),
      name: item.name,
      userId: match?._id != null ? String(match._id) : undefined,
      firstName: match?.firstName,
      lastName: match?.lastName
    });
  };

  // Creator stays first; the staged "pending" row sits directly below them,
  // above the rest of the people with access.
  const ownerEntry = entries.find(
    entry => !!ownerId && entry.userId === ownerId
  );
  const otherEntries = ownerEntry
    ? entries.filter(entry => entry.userId !== ownerEntry.userId)
    : entries;

  const renderShareRow = (entry: RfqInternalShareEntry) => {
    const isOwner = !!ownerId && entry.userId === ownerId;
    const isSelf = !!currentUserId && entry.userId === currentUserId;
    const isRemoved = removedUserIds.includes(entry.userId);
    const strikeStyle = isRemoved
      ? { textDecoration: 'line-through' as const }
      : undefined;

    return (
      <Box
        className={classes.row}
        key={entry.userId}
        style={isRemoved ? { background: '#FDF3F3' } : undefined}>
        <RfqUserAvatar
          size="sm"
          userId={entry.userId}
          value={getInitials({
            firstName: entry.firstName,
            lastName: entry.lastName,
            email: entry.email
          })}
          style={isRemoved ? { opacity: 0.55 } : undefined}
        />
        <Box className={classes.rowText}>
          <SText
            size="sm"
            weight="semibold"
            maxLines={1}
            fontColor={isRemoved ? '#A0AAB4' : undefined}
            style={strikeStyle}
            title={`${entry.firstName} ${entry.lastName}`}
          />
          <SText
            size="xs"
            maxLines={1}
            fontColor="#5D7083"
            style={strikeStyle}
            title={entry.email}
          />
        </Box>

        {isOwner && (
          <Box className={classes.tag}>{isSelf ? 'Owner · You' : 'Owner'}</Box>
        )}
        {!isOwner && isSelf && !isRemoved && (
          <Box className={classes.tag}>You</Box>
        )}
        {!isOwner && isRemoved && (
          <Box className={classes.removedTag}>Removed</Box>
        )}

        {!isOwner &&
          (isRemoved ? (
            <IconButton
              size="small"
              aria-label="Undo remove"
              className={classes.removeBtn}
              onClick={() => onToggleRemove(entry.userId)}>
              <ReplayRoundedIcon fontSize="small" />
            </IconButton>
          ) : (
            <IconButton
              size="small"
              aria-label="Remove access"
              className={classes.removeBtn}
              onClick={() => onToggleRemove(entry.userId)}>
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          ))}
      </Box>
    );
  };

  const pendingRows = pendingUsers.map(pendingUser => (
    <Box
      className={classes.row}
      style={{ background: '#EFF7FE' }}
      key={`pending-${pendingUser.email}`}>
      <RfqUserAvatar
        size="sm"
        userId={pendingUser.userId}
        value={getInitials({
          firstName: pendingUser.firstName,
          lastName: pendingUser.lastName,
          email: pendingUser.email
        })}
      />
      <Box className={classes.rowText}>
        <SText
          size="sm"
          weight="semibold"
          maxLines={1}
          title={pendingUser.name || pendingUser.email}
        />
        <SText
          size="xs"
          maxLines={1}
          fontColor="#5D7083"
          title={pendingUser.email}
        />
      </Box>
      <Box className={classes.tag}>Pending</Box>
      <IconButton
        size="small"
        aria-label="Cancel"
        className={classes.removeBtn}
        onClick={() => onRemovePending(pendingUser.email)}>
        <CloseRoundedIcon fontSize="small" />
      </IconButton>
    </Box>
  ));

  return (
    <Box className={classes.content}>
      <Box>
        <Box className={classes.label}>Add people</Box>
        <SAutocomplete
          name="internal-share-user"
          placeholder="Search by name or email…"
          options={options}
          selected={[]}
          disableCreate
          loading={isLoading}
          handleInputChange={setSearchValue}
          handleItemClick={handleItemClick}
          fetchMore={fetchMore}
        />
        <Box className={classes.hint}>
          Only people in your company can be added.
        </Box>
      </Box>

      <Box>
        <Box className={classes.label}>
          People with access
          {entries.length > 0 && (
            <Box component="span" className={classes.count}>
              {entries.length}
            </Box>
          )}
        </Box>
        {!entries.length && !pendingUsers.length ? (
          <Box className={classes.empty}>
            <PeopleAltOutlinedIcon className={classes.emptyIcon} />
            <SText
              size="sm"
              weight="semibold"
              title={`Only you can see this ${entityLabel}`}
            />
            <SText
              size="xs"
              fontColor="#8A97A5"
              title="Add a teammate above to start collaborating."
            />
          </Box>
        ) : (
          <Box className={classes.list}>
            {ownerEntry && renderShareRow(ownerEntry)}
            {pendingRows}
            {otherEntries.map(renderShareRow)}
          </Box>
        )}
      </Box>
    </Box>
  );
};
